import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Shell } from "@/shell/Shell";
import { ChartTooltip } from "@/features/investors/components/charts/ChartTooltip";
import { Insight } from "@/features/investors/components/atoms/InvestorsAtoms";
import {
    InvestorsLoaderStats,
    InvestorsLoadingOverlay,
} from "@/features/investors/components/loading/InvestorsLoadingOverlay";
import { ExecutiveSummarySection } from "@/features/investors/components/sections/ExecutiveSummarySection";
import { GrowthSection } from "@/features/investors/components/sections/GrowthSection";
import { MonetizationSection } from "@/features/investors/components/sections/MonetizationSection";
import { RetentionSection } from "@/features/investors/components/sections/RetentionSection";
import { CorridorsSection } from "@/features/investors/components/sections/CorridorsSection";
import { OperationsSection } from "@/features/investors/components/sections/OperationsSection";
import { RiskSection } from "@/features/investors/components/sections/RiskSection";
import { NetworkSection } from "@/features/investors/components/sections/NetworkSection";
import { TakeawaysSection } from "@/features/investors/components/sections/TakeawaysSection";
import { useInvestorsDashboard } from "@/features/investors/hooks/use-investors-dashboard";
import { InvestorsDashboardData } from "@/features/investors/types/investors.types";
import { investorsInsights } from "@/features/investors/utils/investors-insights";
import { QUARTER_OPTIONS, findQuarter } from "@/features/investors/utils/quarters";
import "@/features/investors/styles/investors.css";

// TODO: global filters (client, operation type) are not wired yet. When they
// land, extend this state shape and forward the values to investorsApi.
type InvestorsFiltersState = {
    quarterId: string;
};

const nextQuarterLabel = (label: string): string => {
    const match = /^Q([1-4]) (\d{4})$/.exec(label);
    if (match === null) return "Next quarter";
    const quarter = Number(match[1]);
    const year = Number(match[2]);
    return quarter === 4 ? `Q1 ${year + 1}` : `Q${quarter + 1} ${year}`;
};

const loaderStatsFrom = (data: InvestorsDashboardData | null): InvestorsLoaderStats | null => {
    if (data === null) return null;
    return {
        volumeEur: data.executive.gmv.value ?? 0,
        operations: data.executive.completedOperations.value ?? 0,
        activeAccounts: data.executive.activeAccounts.value ?? 0,
    };
};

const Sections: React.FC<{ data: InvestorsDashboardData }> = ({ data }) => {
    const insights = investorsInsights.build(data);
    return (
        <>
            <ExecutiveSummarySection data={data} insights={insights} />
            <GrowthSection data={data} insights={insights} />
            <MonetizationSection data={data} insights={insights} />
            <RetentionSection data={data} insights={insights} />
            <CorridorsSection data={data} insights={insights} />
            <OperationsSection data={data} insights={insights} />
            <RiskSection data={data} insights={insights} />
            <NetworkSection data={data} insights={insights} />
            <TakeawaysSection
                insights={insights}
                nextQuarterLabel={nextQuarterLabel(data.period.label)}
            />
        </>
    );
};

export function InvestorsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const quarter = findQuarter(searchParams.get("q"));
    const { data, loading, isFetching, error, refresh } = useInvestorsDashboard(quarter);
    const [loaderExited, setLoaderExited] = useState(!loading);

    useEffect(() => {
        if (loading) {
            setLoaderExited(false);
        }
    }, [loading]);

    const handleLoaderExited = useCallback(() => setLoaderExited(true), []);

    const selectQuarter = (filters: InvestorsFiltersState) => {
        setSearchParams({ q: filters.quarterId });
    };

    const isEmpty =
        data !== null &&
        data.executive.completedOperations.value === 0 &&
        data.executive.createdOperations.value === 0;

    return (
        <Shell fullBleed bleedClassName="investors-root">
            <ChartTooltip />
            {!loaderExited ? (
                <InvestorsLoadingOverlay
                    quarterLabel={data !== null ? data.period.label : quarter.label}
                    ready={!loading && data !== null}
                    failed={!loading && data === null}
                    stats={loaderStatsFrom(data)}
                    onExited={handleLoaderExited}
                />
            ) : null}
            <main>
                <div className="page-head">
                    <div>
                        <div className="eyebrow">Quarterly investor report</div>
                        <h1>{data !== null ? data.period.label : quarter.label} performance</h1>
                        <p>
                            A top-to-bottom read on growth, monetization, retention, scalability and
                            risk — built for the board.
                        </p>
                    </div>
                    <div className="head-tools">
                        <div className="seg">
                            {QUARTER_OPTIONS.map((option) => (
                                <button
                                    key={option.id}
                                    type="button"
                                    className={option.id === quarter.id ? "on" : undefined}
                                    onClick={() => selectQuarter({ quarterId: option.id })}
                                >
                                    {option.shortLabel}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={refresh}
                            disabled={isFetching}
                            title="Refresh"
                        >
                            <span className="mso material-symbols-outlined" style={{ fontSize: 17 }}>
                                refresh
                            </span>
                            {isFetching ? "Loading…" : "Refresh"}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => window.print()}>
                            <span className="mso material-symbols-outlined" style={{ fontSize: 17 }}>
                                download
                            </span>
                            Export PDF
                        </button>
                    </div>
                </div>

                {data !== null && data.warnings.length > 0 ? (
                    <div style={{ display: "grid", gap: 10, marginBottom: 28 }}>
                        {data.warnings.map((warning) => (
                            <Insight key={warning} warn>
                                {warning}
                            </Insight>
                        ))}
                    </div>
                ) : null}

                {error !== null && data === null ? (
                    <div className="state-block error">
                        <h2>Could not load investor metrics</h2>
                        <p>{error}</p>
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={refresh}
                            style={{ marginTop: 16 }}
                        >
                            <span className="mso material-symbols-outlined" style={{ fontSize: 17 }}>
                                refresh
                            </span>
                            Retry
                        </button>
                    </div>
                ) : loading ? null : isEmpty ? (
                    <div className="state-block">
                        <h2>No operations in {quarter.label}</h2>
                        <p>There is no completed or created activity for this period.</p>
                    </div>
                ) : data !== null ? (
                    <Sections data={data} />
                ) : null}
            </main>
        </Shell>
    );
}
